import { getCsrfToken } from "@/app/_utils/csrf";

// Mirror of FIELD_TYPES in server/command/services/checklist/schema.py.
export const FIELD_TYPES = [
  { value: "checkbox", label: "Checkbox" },
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "time", label: "Time" },
  { value: "date", label: "Date" },
  { value: "select", label: "Select" },
  { value: "phone", label: "Phone" },
  { value: "url", label: "Link" },
] as const;

export type ChecklistFieldType = (typeof FIELD_TYPES)[number]["value"];

export type ChecklistField = {
  key: string;
  label: string;
  type: ChecklistFieldType;
  required: boolean;
  help: string;
  options?: string[];
};

export type ChecklistSection = {
  key: string;
  label: string;
  fields: ChecklistField[];
};

// Groups sections in the sidebar. `icon` is a slug resolved by checklistIcons.ts.
export type ChecklistCategory = {
  key: string;
  label: string;
  icon: string;
  sections: ChecklistSection[];
};

export type ChecklistSchema = {
  version: 2;
  categories: ChecklistCategory[];
};

export type ChecklistTemplate = {
  id: string;
  name: string;
  description: string;
  schema: ChecklistSchema;
  created_at: string;
  updated_at: string;
};

export type ChecklistTemplateInput = Pick<ChecklistTemplate, "name" | "description" | "schema">;

export type ChecklistItemStatus = "na" | "need";

export type ChecklistItemValue = string | number | boolean;

export type ChecklistItemState = {
  value?: ChecklistItemValue;
  note?: string;
  status?: ChecklistItemStatus;
  done?: boolean;
};

/** A show's own copy of a template plus its answers, keyed section > field. */
export type ShowChecklist = {
  id: string;
  show: string;
  template: string | null;
  template_name: string;
  schema: ChecklistSchema;
  values: Record<string, Record<string, ChecklistItemState>>;
  disabled_sections: string[];
  updated_at: string;
};

/** Only the keys present are changed; `null` clears. */
export type ChecklistItemPatch = {
  value?: ChecklistItemValue | null;
  note?: string | null;
  status?: ChecklistItemStatus | null;
  done?: boolean | null;
};

class ChecklistService {
  baseUrl = `${import.meta.env.VITE_API_BASE_URL}checklists/templates/`;

  private jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    };
  }

  getTemplatesAsync = async (): Promise<ChecklistTemplate[]> => {
    const res = await fetch(this.baseUrl, { credentials: "include" });
    if (!res.ok) throw await res.json();
    return await res.json();
  };

  getTemplateAsync = async (id: string): Promise<ChecklistTemplate> => {
    const res = await fetch(`${this.baseUrl}${id}/`, { credentials: "include" });
    if (!res.ok) throw await res.json();
    return await res.json();
  };

  createTemplateAsync = async (body: ChecklistTemplateInput): Promise<ChecklistTemplate> => {
    const res = await fetch(this.baseUrl, {
      method: "POST",
      body: JSON.stringify(body),
      headers: this.jsonHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw await res.json();
    return await res.json();
  };

  updateTemplateAsync = async (
    id: string,
    body: ChecklistTemplateInput
  ): Promise<ChecklistTemplate> => {
    const res = await fetch(`${this.baseUrl}${id}/`, {
      method: "PUT",
      body: JSON.stringify(body),
      headers: this.jsonHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw await res.json();
    return await res.json();
  };

  deleteTemplateAsync = async (id: string): Promise<void> => {
    const res = await fetch(`${this.baseUrl}${id}/`, {
      method: "DELETE",
      headers: { "X-CSRFToken": getCsrfToken() },
      credentials: "include",
    });
    if (!res.ok) throw await res.json().catch(() => ({}));
  };

  private showUrl = `${import.meta.env.VITE_API_BASE_URL}show/checklist/`;

  private async send(path: string, method: string, body: object): Promise<ShowChecklist> {
    const res = await fetch(`${this.showUrl}${path}`, {
      method,
      body: JSON.stringify(body),
      headers: this.jsonHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw await res.json().catch(() => ({}));
    return await res.json();
  }

  getShowChecklistAsync = async (showId: string): Promise<ShowChecklist | null> => {
    const res = await fetch(`${this.showUrl}?show_id=${showId}`, { credentials: "include" });
    if (!res.ok) throw await res.json().catch(() => ({}));
    return await res.json();
  };

  attachTemplateAsync = (showId: string, templateId: string) =>
    this.send("attach/", "POST", { show_id: showId, template_id: templateId });

  setItemAsync = (showId: string, section: string, field: string, patch: ChecklistItemPatch) =>
    this.send("item/", "PATCH", { show_id: showId, section, field, ...patch });

  setSectionEnabledAsync = (showId: string, section: string, enabled: boolean) =>
    this.send("section/", "PATCH", { show_id: showId, section, enabled });

  addItemAsync = (showId: string, section: string, label: string, type: ChecklistFieldType, options?: string[]) =>
    this.send("item/add/", "POST", { show_id: showId, section, label, type, options });
}

export default new ChecklistService();
